import { Modal } from './ui/Modal';
import type { ProjectUsageResponse, AiUsageLogEntry } from '@historyjki/shared';

const OPERATION_LABELS: Record<string, string> = {
  'split-story': 'Split story',
  'extract-elements': 'Extract characters',
  'generate-image': 'Generate image',
  'generate-variants': 'Generate variants',
  'refine-image': 'Refine image',
  'accept-refinement': 'Accept refinement',
  'consistency-check': 'Consistency check',
  'refine-text': 'Refine text',
  'describe-image': 'Describe image',
  'modify-description': 'Modify description',
  'suggest-text-placement': 'Suggest text placement',
};

function labelOperation(op: string): string {
  return OPERATION_LABELS[op] ?? op.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatCostUsd(usd: number): string {
  if (usd === 0) return '$0.00';
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  return `$${usd.toFixed(2)}`;
}

function formatTokens(entry: AiUsageLogEntry): string {
  if (entry.image_count != null) return `${entry.image_count} image${entry.image_count !== 1 ? 's' : ''}`;
  const input = entry.input_tokens ?? 0;
  const output = entry.output_tokens ?? 0;
  return `${(input + output).toLocaleString()} tok`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

interface AiUsageModalProps {
  open: boolean;
  onClose: () => void;
  usage: ProjectUsageResponse | null;
}

export function AiUsageModal({ open, onClose, usage }: AiUsageModalProps) {
  const log = usage?.log ?? [];
  const totalUsd = usage?.total_usd ?? 0;

  return (
    <Modal open={open} onClose={onClose} title="AI Usage" size="md">
      <div className="ai-usage">
        <div className="ai-usage__total">{formatCostUsd(totalUsd)}</div>

        {log.length === 0 ? (
          <p className="ai-usage__empty">No AI usage recorded yet</p>
        ) : (
          <table className="ai-usage__table">
            <thead>
              <tr>
                <th>Date / time</th>
                <th>Operation</th>
                <th>Model</th>
                <th>Usage</th>
                <th>Cost</th>
              </tr>
            </thead>
            <tbody>
              {log.map((entry) => (
                <tr key={entry.id}>
                  <td className="ai-usage__date">{formatDate(entry.created_at)}</td>
                  <td>{labelOperation(entry.operation)}</td>
                  <td className="ai-usage__model">{entry.model}</td>
                  <td className="ai-usage__tokens">{formatTokens(entry)}</td>
                  <td className="ai-usage__cost">{formatCostUsd(entry.cost_usd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Modal>
  );
}
